import React from 'react';
import {

  Box,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import { Block, Logout, Email } from '@mui/icons-material';
import { User } from 'firebase/auth';
import { Logo } from '../widgets/Logo';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import { font } from '../../utils/fontHelper';

interface AccessDeniedScreenProps {
  user: User;
  rejectionNote?: string;
  onLogout: () => void;
}

const AccessDeniedScreen: React.FC<AccessDeniedScreenProps> = ({
  user,
  rejectionNote,
  onLogout,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        p: { xs: 2, sm: 3, md: 4 },
        textAlign: 'center',
      }}
    >
      <ResponsiveContainer maxWidth={500}>
        {/* Logo */}
        <Box sx={{ mb: { xs: 4, sm: 6 } }}>
          <Logo size="large" variant="default" state="static" />
        </Box>

        {/* Denied Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: isDark ? 'rgba(255,69,58,0.1)' : 'rgba(255,69,58,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            mx: 'auto',
            border: `2px solid ${theme.palette.error.main}40`,
          }}
        >
          <Block
            sx={{
              fontSize: 40,
              color: theme.palette.error.main,
            }}
          />
        </Box>

        {/* Status Message */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 2,
            fontFamily: font(),
          }}
        >
          Access Not Approved
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            color: theme.palette.text.secondary,
            mb: 3,
            maxWidth: 400,
            mx: 'auto',
            lineHeight: 1.6,
            fontFamily: font(),
          }}
        >
          {rejectionNote ||
            "Your access request was not approved at this time. We're currently in a limited beta and can only accept a small number of users."}
        </Typography>

        {/* User Info */}
        <Box
          sx={{
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            borderRadius: 2,
            p: 2,
            mb: 4,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: 14,
            }}
          >
            Signed in as <strong>{user.email}</strong>
          </Typography>
        </Box>

        {/* Contact Support */}
        <Typography
          sx={{
            fontSize: 14,
            color: theme.palette.text.secondary,
            mb: 3,
            fontFamily: font(),
          }}
        >
          If you believe this is a mistake, please contact support.
        </Typography>

        {/* Sign out button */}
        <Button
          variant="contained"
          size="large"
          startIcon={<Logout />}
          onClick={onLogout}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: '#FFFFFF',
            borderRadius: 3,
            py: 1.25,
            px: 4,
            fontSize: 15,
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: font(),
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
            },
          }}
        >
          Sign Out
        </Button>
      </ResponsiveContainer>
    </Box>
  );
};

export default AccessDeniedScreen;
